import { useEffect, useRef, useState } from "react";
import { createClient } from "@supabase/supabase-js";
import { BrowserRouter as Router, Routes, Route, Link, useParams, useLocation } from "react-router-dom";
import ActivityPage from "./ActivityPage";
import { initGA, trackPageView, trackUserAction, trackActivityInteraction, trackEventManagement, trackChatInteraction } from "./analytics";

const CustomDatePicker = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(value ? new Date(value) : new Date());
  const [selectedTime, setSelectedTime] = useState(value ? value.split('T')[1] || '12:00' : '12:00');

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const today = new Date();
  const currentMonth = selectedDate.getMonth();
  const currentYear = selectedDate.getFullYear();
  
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
  const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
  const startDate = new Date(firstDayOfMonth);
  startDate.setDate(startDate.getDate() - firstDayOfMonth.getDay());

  const days = [];
  for (let i = 0; i < 42; i++) {
    const date = new Date(startDate);
    date.setDate(startDate.getDate() + i);
    days.push(date);
  }

  const handleDateSelect = (date) => {
    const formattedDate = date.toISOString().split('T')[0];
    const dateTimeValue = `${formattedDate}T${selectedTime}`;
    setSelectedDate(date);
    onChange(dateTimeValue);
    setIsOpen(false);
  };

  const handleTimeChange = (time) => {
    setSelectedTime(time);
    const formattedDate = selectedDate.toISOString().split('T')[0];
    const dateTimeValue = `${formattedDate}T${time}`;
    onChange(dateTimeValue);
  };

  const formatDisplayDate = () => {
    if (!value) return 'Select date and time';
    const date = new Date(value);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setMonth(currentMonth + direction);
    setSelectedDate(newDate);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-3 border rounded-lg text-left focus:outline-none focus:ring-2 focus:ring-coral bg-white flex items-center justify-between"
      >
        <span className={value ? 'text-gray-900' : 'text-gray-500'}>
          {formatDisplayDate()}
        </span>
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
          <line x1="16" y1="2" x2="16" y2="6"></line>
          <line x1="8" y1="2" x2="8" y2="6"></line>
          <line x1="3" y1="10" x2="21" y2="10"></line>
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 bg-white border rounded-lg shadow-lg z-10 p-4 w-80">
          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={() => navigateMonth(-1)}
              className="p-1 rounded hover:bg-gray-100"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15,18 9,12 15,6"></polyline>
              </svg>
            </button>
            <h3 className="font-semibold">
              {months[currentMonth]} {currentYear}
            </h3>
            <button
              type="button"
              onClick={() => navigateMonth(1)}
              className="p-1 rounded hover:bg-gray-100"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9,18 15,12 9,6"></polyline>
              </svg>
            </button>
          </div>

          {/* Days of Week */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
              <div key={day} className="text-xs font-medium text-gray-500 text-center p-2">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div className="grid grid-cols-7 gap-1 mb-4">
            {days.map((day, index) => {
              const isCurrentMonth = day.getMonth() === currentMonth;
              const isToday = day.toDateString() === today.toDateString();
              const isSelected = value && day.toDateString() === new Date(value).toDateString();
              
              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleDateSelect(day)}
                  className={`
                    p-2 text-sm rounded hover:bg-gray-100 transition-colors
                    ${!isCurrentMonth ? 'text-gray-400' : 'text-gray-900'}
                    ${isToday ? 'bg-blue-100 font-semibold' : ''}
                    ${isSelected ? 'bg-coral text-white' : ''}
                  `}
                  style={isSelected ? {backgroundColor: 'var(--eventally-coral)'} : {}}
                >
                  {day.getDate()}
                </button>
              );
            })}
          </div>

          {/* Time Selection */}
          <div className="border-t pt-3">
            <label className="block text-sm font-medium mb-2" style={{color: 'var(--eventally-dark)'}}>Time</label>
            <input
              type="time"
              value={selectedTime}
              onChange={(e) => handleTimeChange(e.target.value)}
              className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-coral"
            />
          </div>
        </div>
      )}
    </div>
  );
};

const ProfilePage = () => {
  const [session, setSession] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [showCreateEventModal, setShowCreateEventModal] = useState(false);
  const [userEvents, setUserEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [showTelegramModal, setShowTelegramModal] = useState(false);
  const [telegramUsername, setTelegramUsername] = useState('');
  const [telegramInput, setTelegramInput] = useState('');
  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    date: '',
    location: '',
    timezone: 'Europe/Berlin'
  });

  useEffect(() => {
    if (IS_LOCAL_RUN) {
      const mockSession = {
        user: {
          id: process.env.REACT_APP_LOCAL_USER_ID || 'local-user-123',
          email: process.env.REACT_APP_LOCAL_USER_EMAIL || 'local@example.com'
        }
      };
      setSession(mockSession);
      setLoadingAuth(false);
      return;
    }

    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session ?? null);
      setLoadingAuth(false);
    });
  }, []);

  // Fetch user events when session is available
  useEffect(() => {
    if (session) {
      fetchUserEvents();
    }
  }, [session]);

  // Load saved Telegram username from localStorage
  useEffect(() => {
    const savedUsername = localStorage.getItem('eventally_telegram_username');
    if (savedUsername) {
      setTelegramUsername(savedUsername);
    }
  }, []);

  const handleTelegramSave = async () => {
    const username = telegramInput.replace('@', ''); // Remove @ if user types it
    if (username.trim()) {
      try {
        const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
        if (apiBaseUrl) {
          const response = await fetch(`${apiBaseUrl}/save_event`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: IS_LOCAL_RUN ? LOCAL_USER_ID : session.user.id,
              event_type: 'tg_linked',
              event: {
                user_tg: `@${username}`
              }
            }),
          });
          
          if (response.ok) {
            localStorage.setItem('eventally_telegram_username', username);
            setTelegramUsername(username);
            setTelegramInput('');
            setShowTelegramModal(false);
            trackUserAction('telegram_username_saved', { label: username });
          } else {
            alert('Failed to link Telegram account');
            console.error('Telegram link failed:', response.statusText);
          }
        }
      } catch (error) {
        alert('Error linking Telegram account');
        console.error('Error linking Telegram:', error);
      }
    }
  };

  const handleTelegramModalOpen = () => {
    setTelegramInput(telegramUsername ? `@${telegramUsername}` : '@');
    setShowTelegramModal(true);
    trackUserAction('telegram_modal_opened');
  };

  const fetchUserEvents = async () => {
    setLoadingEvents(true);
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
      if (!apiBaseUrl) {
        console.error('REACT_APP_API_BASE_URL not configured');
        setLoadingEvents(false);
        return;
      }

      // Use constant UUID for local development
      const userId = IS_LOCAL_RUN ? LOCAL_USER_ID : (session?.user?.id || LOCAL_USER_ID);
      const response = await fetch(`${apiBaseUrl}/activities/user/${userId}`);
      
      if (response.ok) {
        const data = await response.json();
        setUserEvents(data.activities || data || []);
      } else {
        console.error('Error fetching user events:', response.statusText);
        setUserEvents([]);
      }
    } catch (error) {
      console.error('Error fetching user events:', error);
      setUserEvents([]);
    } finally {
      setLoadingEvents(false);
    }
  };

  const handleFormChange = (field, value) => {
    setEventForm(prev => ({ ...prev, [field]: value }));
  };

  const showDeleteConfirmation = (activityId, eventTitle) => {
    setEventToDelete({ id: activityId, title: eventTitle });
    setShowDeleteConfirm(true);
  };

  const deleteEvent = async () => {
    if (!eventToDelete) return;
    
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
      if (!apiBaseUrl) {
        console.error('REACT_APP_API_BASE_URL not configured');
        return;
      }

      const response = await fetch(`${apiBaseUrl}/activities/${eventToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        trackEventManagement('event_deleted', 'private_event');
        fetchUserEvents();
        setShowDeleteConfirm(false);
        setEventToDelete(null);
      } else {
        alert('Failed to delete event');
        console.error('Delete failed:', response.statusText);
      }
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('Error deleting event');
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setEventToDelete(null);
  };

  const callCalendarLinkAPI = async (eventData) => {
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
      if (!apiBaseUrl) return;

      // Convert date to required format and calculate end time (2 hours later)
      const startDate = new Date(eventData.date);
      const endDate = new Date(startDate.getTime() + 2 * 60 * 60 * 1000); // Add 2 hours

      const formatDateTime = (date) => {
        return date.toISOString().replace(/[-:]/g, '').split('.')[0];
      };

      const response = await fetch(`${apiBaseUrl}/calendar_link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_name: eventData.title,
          start_datetime: formatDateTime(startDate),
          end_datetime: formatDateTime(endDate),
          location: eventData.location || 'TBD',
          details: eventData.description,
          timezone: eventData.timezone
        }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Calendar link created:', result);
      } else {
        console.error('Calendar link API failed:', response.statusText);
      }
    } catch (error) {
      console.error('Error calling calendar link API:', error);
    }
  };

  const handleOpenModal = () => {
    setShowCreateEventModal(true);
  };

  const handleCreateEvent = async () => {
    if (!eventForm.title || !eventForm.description || !eventForm.date) {
      alert('Please fill in all fields');
      return;
    }

    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
      if (apiBaseUrl) {
        const response = await fetch(`${apiBaseUrl}/save_event`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: IS_LOCAL_RUN ? '86e4b0b2-def3-44af-949e-42db4a81b1b2' : session.user.id,
            event_type: 'private_event',
            event: {
              title: eventForm.title,
              description: eventForm.description,
              date: eventForm.date,
              user_email: session.user.email
            }
          }),
        });

        if (response.ok) {
          trackEventManagement('event_created', 'private_event');
          // Also call calendar link API when event is successfully created
          await callCalendarLinkAPI(eventForm);
          
          setEventForm({ title: '', description: '', date: '', location: '', timezone: 'Europe/Berlin' });
          setShowCreateEventModal(false);
          
          // Refresh user events list
          fetchUserEvents();
        } else {
          alert('Failed to create event');
        }
      }
    } catch (error) {
      console.error('Error creating event:', error);
      alert('Error creating event');
    }
  };

  if (loadingAuth) return <ScreenCenter>Loading…</ScreenCenter>;
  if (!session) return <SignIn />;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="eventally-gradient-bg px-4 py-6">
        <div className="max-w-4xl mx-auto">
          <Link 
            to="/" 
            className="inline-flex items-center mb-4 px-3 py-1 rounded-lg bg-white hover:shadow-lg transition-all"
            style={{color: 'var(--eventally-dark)'}}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
              <polyline points="15,18 9,12 15,6"></polyline>
            </svg>
            Back to EventAlly
          </Link>
          <h1 className="eventally-brand text-3xl font-bold" style={{color: 'var(--eventally-dark)'}}>
            Profile
          </h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold mb-4" style={{color: 'var(--eventally-dark)'}}>User Information</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{color: 'var(--eventally-dark)'}}>Email</label>
                <div className="p-3 bg-gray-50 rounded-lg border">
                  {session.user.email}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1" style={{color: 'var(--eventally-dark)'}}>User ID</label>
                <div className="p-3 bg-gray-50 rounded-lg border font-mono text-sm">
                  {session.user.id}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{color: 'var(--eventally-dark)'}}>Telegram</label>
                <div className="flex items-center gap-3">
                  <div className="flex-1 p-3 bg-gray-50 rounded-lg border">
                    {telegramUsername ? `@${telegramUsername}` : 'Not linked'}
                  </div>
                  <button
                    onClick={handleTelegramModalOpen}
                    className="eventally-button py-2 px-4 rounded-lg font-medium text-white text-sm"
                  >
                    {telegramUsername ? 'Update' : 'Link to telegram'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Bot Reminder */}
          <div className="pt-6 mb-6">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <span style={{color: 'var(--eventally-dark)'}}>Test the bot: </span>
              <a 
                href="https://t.me/EventAllyBot" 
                target="_blank" 
                rel="noopener noreferrer"
                className="eventally-brand font-semibold activity-content"
                style={{color: 'var(--eventally-coral)', textDecoration: 'underline'}}
              >
                EventAlly
              </a>
            </div>
          </div>

          <div className="border-t pt-6 mb-8">
            <h3 className="text-lg font-semibold mb-3" style={{color: 'var(--eventally-dark)'}}>Create Events</h3>
            <p className="text-gray-600 mb-4">
              Create and manage your own events.
            </p>
            <button 
              onClick={handleOpenModal}
              className="eventally-button py-2 px-4 rounded-lg font-medium text-white mb-4"
            >
              Create Event
            </button>

            {/* User Events List */}
            <div>
              <h4 className="text-md font-semibold mb-3" style={{color: 'var(--eventally-dark)'}}>Your Events</h4>
              {loadingEvents ? (
                <div className="text-center py-4">
                  <div className="text-gray-500">Loading your events...</div>
                </div>
              ) : userEvents.length > 0 ? (
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {userEvents.map((event, index) => (
                    <div key={event.id || index} className="bg-gray-50 rounded-lg p-3 border">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          {event.id ? (
                            <Link
                              to={`/${event.id}`}
                              className="font-medium hover:underline cursor-pointer"
                              style={{color: 'var(--eventally-dark)'}}
                            >
                              {event.title || event.event_name || 'Untitled Event'}
                            </Link>
                          ) : (
                            <h5 className="font-medium" style={{color: 'var(--eventally-dark)'}}>
                              {event.title || event.event_name || 'Untitled Event'}
                            </h5>
                          )}
                          {event.description && (
                            <p className="text-sm text-gray-600 mt-1">{event.description}</p>
                          )}
                          {event.date && (
                            <p className="text-xs text-gray-500 mt-2">
                              📅 {new Date(event.date).toLocaleDateString('en-US', {
                                weekday: 'short',
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          )}
                          <span className="text-xs px-2 py-1 rounded inline-block mt-2" style={{backgroundColor: 'var(--eventally-light-coral)', color: 'var(--eventally-dark)'}}>
                            Private
                          </span>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          {event.id && (
                            <button
                              onClick={() => showDeleteConfirmation(event.id, event.title || event.event_name || 'Untitled Event')}
                              className="text-xs px-2 py-1 rounded bg-red-500 hover:bg-red-600 text-white transition-colors"
                              title="Delete event"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-gray-500">
                  <p>No events created yet.</p>
                  <p className="text-sm">Click "Create Event" to add your first event!</p>
                </div>
              )}
            </div>
          </div>

          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold mb-3" style={{color: 'var(--eventally-dark)'}}>Account Settings</h3>
            <p className="text-gray-600 mb-4">
              Manage your EventAlly account preferences and settings.
            </p>
            {!IS_LOCAL_RUN && (
              <button 
                onClick={() => supabase.auth.signOut()}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors"
              >
                Sign Out
              </button>
            )}
          </div>
        </div>
      </main>

      {/* Create Event Modal */}
      {showCreateEventModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-semibold" style={{color: 'var(--eventally-dark)'}}>Create Event</h2>
              <button
                onClick={() => setShowCreateEventModal(false)}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={{color: 'var(--eventally-dark)'}}>Title</label>
                <input
                  type="text"
                  value={eventForm.title}
                  onChange={(e) => handleFormChange('title', e.target.value)}
                  className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-coral"
                  placeholder="Enter event title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{color: 'var(--eventally-dark)'}}>Description</label>
                <textarea
                  value={eventForm.description}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                  rows="4"
                  className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-coral resize-none"
                  placeholder="Enter event description"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{color: 'var(--eventally-dark)'}}>Location</label>
                <input
                  type="text"
                  value={eventForm.location}
                  onChange={(e) => handleFormChange('location', e.target.value)}
                  className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-coral"
                  placeholder="Enter event location (optional)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{color: 'var(--eventally-dark)'}}>Date & Time</label>
                <CustomDatePicker
                  value={eventForm.date}
                  onChange={(value) => handleFormChange('date', value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{color: 'var(--eventally-dark)'}}>Timezone</label>
                <select
                  value={eventForm.timezone}
                  onChange={(e) => handleFormChange('timezone', e.target.value)}
                  className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-coral"
                >
                  <option value="Europe/Berlin">Europe/Berlin</option>
                  <option value="America/New_York">America/New_York</option>
                  <option value="America/Los_Angeles">America/Los_Angeles</option>
                  <option value="Asia/Tokyo">Asia/Tokyo</option>
                  <option value="UTC">UTC</option>
                </select>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t">
              <button
                onClick={() => setShowCreateEventModal(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateEvent}
                className="eventally-button py-2 px-4 rounded-lg font-medium text-white"
              >
                Create Event
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-sm shadow-2xl border-0">
            {/* Modal Header */}
            <div className="p-6 pb-4">
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full" style={{backgroundColor: 'var(--eventally-light-coral)'}}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'var(--eventally-coral)'}}>
                  <path d="M3 6h18"></path>
                  <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                  <path d="m19 6-1 14a2 2 0 0 1-2-2H8a2 2 0 0 1-2-2L5 6"></path>
                  <line x1="10" y1="11" x2="10" y2="17"></line>
                  <line x1="14" y1="11" x2="14" y2="17"></line>
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-center text-gray-900 mb-2">Delete Event</h3>
              <p className="text-gray-600 text-center">
                Are you sure you want to delete "{eventToDelete?.title}"? This action cannot be undone.
              </p>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center gap-3 p-6 pt-2">
              <button
                onClick={cancelDelete}
                className="flex-1 px-4 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={deleteEvent}
                className="flex-1 px-4 py-3 text-white bg-red-500 hover:bg-red-600 rounded-lg font-medium transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Telegram Username Modal */}
      {showTelegramModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-sm shadow-2xl border-0">
            {/* Modal Header */}
            <div className="p-6 pb-4">
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full" style={{backgroundColor: 'var(--eventally-light-coral)'}}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'var(--eventally-coral)'}}>
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-center text-gray-900 mb-2">Link Telegram Account</h3>
              <p className="text-gray-600 text-center text-sm">
                Enter your Telegram username to link your account
              </p>
            </div>

            {/* Modal Content */}
            <div className="px-6 pb-4">
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2" style={{color: 'var(--eventally-dark)'}}>
                  Telegram Username
                </label>
                <input
                  type="text"
                  value={telegramInput}
                  onChange={(e) => setTelegramInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleTelegramSave()}
                  className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-coral text-gray-700"
                  style={{color: 'var(--eventally-dark)'}}
                  placeholder="@username"
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-1">
                  Your Telegram username (without spaces)
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center gap-3 p-6 pt-2">
              <button
                onClick={() => {
                  setShowTelegramModal(false);
                  setTelegramInput('');
                }}
                className="flex-1 px-4 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleTelegramSave}
                disabled={!telegramInput.trim() || telegramInput === '@'}
                className="flex-1 px-4 py-3 text-white rounded-lg font-medium transition-colors eventally-button disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SubscriptionPage = () => {
  const [session, setSession] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [isSubscribing, setIsSubscribing] = useState(false);

  useEffect(() => {
    if (IS_LOCAL_RUN) {
      const mockSession = {
        user: {
          id: process.env.REACT_APP_LOCAL_USER_ID || 'local-user-123',
          email: process.env.REACT_APP_LOCAL_USER_EMAIL || 'local@example.com'
        }
      };
      setSession(mockSession);
      setLoadingAuth(false);
      return;
    }

    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session ?? null);
      setLoadingAuth(false);
    });
  }, []);

  const handleSubscribe = async () => {
    setIsSubscribing(true);

    try {
      // Add subscription logic here
      console.log('Subscribe button clicked for user:', session.user.email);
      alert('Subscription feature coming soon!');
    } catch (error) {
      console.error('Subscription error:', error);
      alert('Subscription failed. Please try again.');
    } finally {
      setIsSubscribing(false);
    }
  };

  if (loadingAuth) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-lg">Please sign in to access subscriptions.</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="eventally-gradient-bg px-4 py-6">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center mb-4 px-3 py-1 rounded-lg bg-white hover:shadow-lg transition-all"
            style={{color: 'var(--eventally-dark)'}}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
              <polyline points="15,18 9,12 15,6"></polyline>
            </svg>
            Back to EventAlly
          </Link>
          <h1 className="eventally-brand text-3xl font-bold" style={{color: 'var(--eventally-dark)'}}>
            Subscription
          </h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="text-center">
            <h2 className="text-2xl font-semibold mb-4" style={{color: 'var(--eventally-dark)'}}>
              Upgrade Your EventAlly Experience
            </h2>
            <p className="text-gray-600 mb-8 text-lg">
              Get access to premium features and enhanced event management capabilities.
            </p>

            <button
              onClick={handleSubscribe}
              disabled={isSubscribing}
              className="eventally-button py-3 px-8 rounded-lg font-medium text-white text-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubscribing ? 'Processing...' : 'Subscribe'}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

// ---- CONFIG ----
const WEBHOOK_URL = process.env.REACT_APP_WEBHOOK_URL;
const IS_LOCAL_RUN = process.env.REACT_APP_IS_LOCAL_RUN === 'true';
const SHOW_CHAT = process.env.REACT_APP_SHOW_CHAT === 'true';
const LOCAL_USER_ID = '86e4b0b2-def3-44af-949e-42db4a81b1b2';

// Simple UUID generator for compatibility
const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY,
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
  }
);

function HomePage() {
  const [session, setSession] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(true);

  useEffect(() => {
    if (IS_LOCAL_RUN) {
      // Create mock session for local development
      const mockSession = {
        user: {
          id: process.env.REACT_APP_LOCAL_USER_ID || 'local-user-123',
          email: process.env.REACT_APP_LOCAL_USER_EMAIL || 'local@example.com'
        }
      };
      setSession(mockSession);
      setLoadingAuth(false);
      return;
    }

    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session ?? null);
      setLoadingAuth(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, sess) => {
      setSession(sess ?? null);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  // Manual refresh every 7 days (in addition to Supabase auto refresh)
  useEffect(() => {
    if (!session || IS_LOCAL_RUN) return;
    const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
    const id = setInterval(() => {
      supabase.auth.refreshSession().catch(() => {});
    }, WEEK_MS);
    return () => clearInterval(id);
  }, [session]);

  if (loadingAuth) return <ScreenCenter>Loading…</ScreenCenter>;
  if (!session) return <SignIn />;
  return <Chat session={session} />;
}

function SignIn() {
  const [submitting, setSubmitting] = useState(false);

  const signWithGoogle = async () => {
    setSubmitting(true);
    trackUserAction('sign_in_attempted', { label: 'google_oauth' });
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          queryParams: { prompt: "consent" },
          redirectTo: process.env.REACT_APP_SITE_URL || window.location.origin,
        },
      });
      if (error) throw error;
      trackUserAction('sign_in_successful', { label: 'google_oauth' });
      // Redirect handled by Supabase
    } catch (e) {
      trackUserAction('sign_in_failed', { label: 'google_oauth' });
      alert((e && e.message) || "Google Sign-in failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ScreenCenter>
      <div className="flex flex-col items-center gap-8 w-full max-w-2xl px-4">
        {/* EventAlly Logo */}
        <div className="flex flex-col items-center gap-4">
          <img
            src="/event_ally_logo.jpeg"
            alt="EventAlly Logo"
            className="w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 object-contain rounded-2xl shadow-lg"
          />
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl font-bold eventally-brand mb-2" style={{color: 'var(--eventally-dark)'}}>Welcome to EventAlly</h1>
            <p className="text-base sm:text-lg text-gray-600 mb-6">Your AI-powered event discovery and management platform</p>

            {/* Sign in Button - Early Placement */}
            <div className="flex flex-col items-center gap-4 mb-8">
              <button
                onClick={signWithGoogle}
                disabled={submitting}
                className="w-full max-w-sm text-white py-4 px-6 rounded-lg font-medium flex items-center justify-center gap-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed eventally-button text-lg"
              >
                <LoginIcon />
                <span>Sign in with Google</span>
              </button>
              <p className="text-xs text-gray-500 text-center max-w-sm">
                Sign in to access all features, sync your events, and get personalized recommendations
              </p>
            </div>

            {/* Brief Description */}
            <div className="text-gray-600 text-sm sm:text-base max-w-lg mx-auto space-y-3">
              <p>
                Discover exciting events around you, chat with our AI assistant, and create your own personal events - all in one place.
              </p>
            </div>
          </div>
        </div>

        {/* Features Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-2xl">
          {/* Web Version Features */}
          <div className="bg-white rounded-lg p-6 shadow-lg border">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{backgroundColor: 'var(--eventally-light-coral)'}}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'var(--eventally-coral)'}}>
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                  <line x1="8" y1="21" x2="16" y2="21"></line>
                  <line x1="12" y1="17" x2="12" y2="21"></line>
                </svg>
              </div>
              <h3 className="font-semibold text-lg" style={{color: 'var(--eventally-dark)'}}>Web Version</h3>
            </div>
            <ul className="text-gray-600 text-sm space-y-2">
              <li className="flex items-start gap-2">
                <span style={{color: 'var(--eventally-coral)'}}>•</span>
                <span>Explore and discover local events</span>
              </li>
              <li className="flex items-start gap-2">
                <span style={{color: 'var(--eventally-coral)'}}>•</span>
                <span>Create and manage personal events</span>
              </li>
              <li className="flex items-start gap-2">
                <span style={{color: 'var(--eventally-coral)'}}>•</span>
                <span>Chat with AI for event recommendations</span>
              </li>
              <li className="flex items-start gap-2">
                <span style={{color: 'var(--eventally-coral)'}}>•</span>
                <span>Bookmark events and sync with calendar</span>
              </li>
            </ul>
          </div>

          {/* Telegram Bot Features */}
          <div className="bg-white rounded-lg p-6 shadow-lg border">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{backgroundColor: 'var(--eventally-light-coral)'}}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'var(--eventally-coral)'}}>
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
              </div>
              <h3 className="font-semibold text-lg" style={{color: 'var(--eventally-dark)'}}>Telegram Bot</h3>
            </div>
            <ul className="text-gray-600 text-sm space-y-2 mb-4">
              <li className="flex items-start gap-2">
                <span style={{color: 'var(--eventally-coral)'}}>•</span>
                <span>Get event notifications on-the-go</span>
              </li>
              <li className="flex items-start gap-2">
                <span style={{color: 'var(--eventally-coral)'}}>•</span>
                <span>Quick event search and discovery</span>
              </li>
              <li className="flex items-start gap-2">
                <span style={{color: 'var(--eventally-coral)'}}>•</span>
                <span>AI-powered event assistance</span>
              </li>
            </ul>
            <a
              href="https://t.me/EventAllyBot"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg border hover:shadow-md transition-shadow"
              style={{color: 'var(--eventally-dark)', borderColor: 'var(--eventally-coral)'}}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
              Try Telegram Bot
            </a>
          </div>
        </div>

        {/* Screenshots Section */}
        <div className="w-full max-w-4xl">
          <div className="text-center mb-6">
            <h3 className="text-xl font-semibold mb-2" style={{color: 'var(--eventally-dark)'}}>
              See EventAlly in Action
            </h3>
            <p className="text-gray-600 text-sm">
              Discover how EventAlly makes event management effortless
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Screenshot 1 - Activity Discovery */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="aspect-[9/16] relative">
                <img
                  src="/screenshot2.jpeg"
                  alt="EventAlly Activity Discovery"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-4">
                <h4 className="font-semibold text-sm mb-2" style={{color: 'var(--eventally-dark)'}}>
                  Discover Local Events
                </h4>
                <p className="text-xs text-gray-600">
                  Browse and filter activities by category, location, and interests with our intuitive interface
                </p>
              </div>
            </div>

            {/* Screenshot 2 - Event Details */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="aspect-[9/16] relative">
                <img
                  src="/screenshot3.jpeg"
                  alt="EventAlly Event Details"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-4">
                <h4 className="font-semibold text-sm mb-2" style={{color: 'var(--eventally-dark)'}}>
                  Detailed Event Info
                </h4>
                <p className="text-xs text-gray-600">
                  Get comprehensive event details including categories, timing, location, and descriptions
                </p>
              </div>
            </div>

            {/* Screenshot 3 - Calendar Integration */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="aspect-[9/16] relative">
                <img
                  src="/screenshot1.jpeg"
                  alt="EventAlly Calendar Integration"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-4">
                <h4 className="font-semibold text-sm mb-2" style={{color: 'var(--eventally-dark)'}}>
                  Calendar Integration
                </h4>
                <p className="text-xs text-gray-600">
                  Seamlessly sync events to your personal calendar with automatic notifications and reminders
                </p>
              </div>
            </div>
          </div>
        </div>

      </div>
    </ScreenCenter>
  );
}

function SignInPage() {
  const [submitting, setSubmitting] = useState(false);

  const signWithGoogle = async () => {
    setSubmitting(true);
    trackUserAction('sign_in_attempted', { label: 'google_oauth' });
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          queryParams: { prompt: "consent" },
          redirectTo: process.env.REACT_APP_SITE_URL || window.location.origin,
        },
      });
      if (error) throw error;
      trackUserAction('sign_in_successful', { label: 'google_oauth' });
      // Redirect handled by Supabase
    } catch (e) {
      trackUserAction('sign_in_failed', { label: 'google_oauth' });
      alert((e && e.message) || "Google Sign-in failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ScreenCenter>
      <div className="flex flex-col items-center gap-8 w-full max-w-2xl px-4">
        {/* EventAlly Logo */}
        <div className="flex flex-col items-center gap-4">
          <img
            src="/event_ally_logo.jpeg"
            alt="EventAlly Logo"
            className="w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 object-contain rounded-2xl shadow-lg"
          />
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl font-bold eventally-brand mb-2" style={{color: 'var(--eventally-dark)'}}>Welcome to EventAlly</h1>
            <p className="text-base sm:text-lg text-gray-600 mb-6">Your AI-powered event discovery and management platform</p>

            {/* Sign in Button - Early Placement */}
            <div className="flex flex-col items-center gap-4 mb-8">
              <button
                onClick={signWithGoogle}
                disabled={submitting}
                className="w-full max-w-sm text-white py-4 px-6 rounded-lg font-medium flex items-center justify-center gap-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed eventally-button text-lg"
              >
                <LoginIcon />
                <span>Sign in with Google</span>
              </button>
              <p className="text-xs text-gray-500 text-center max-w-sm">
                Sign in to access all features, sync your events, and get personalized recommendations
              </p>
            </div>

            {/* Brief Description */}
            <div className="text-gray-600 text-sm sm:text-base max-w-lg mx-auto space-y-3">
              <p>
                Discover exciting events around you, chat with our AI assistant, and create your own personal events - all in one place.
              </p>
            </div>
          </div>
        </div>

        {/* Features Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-2xl">
          {/* Web Version Features */}
          <div className="bg-white rounded-lg p-6 shadow-lg border">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{backgroundColor: 'var(--eventally-light-coral)'}}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'var(--eventally-coral)'}}>
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                  <line x1="8" y1="21" x2="16" y2="21"></line>
                  <line x1="12" y1="17" x2="12" y2="21"></line>
                </svg>
              </div>
              <h3 className="font-semibold text-lg" style={{color: 'var(--eventally-dark)'}}>Web Version</h3>
            </div>
            <ul className="text-gray-600 text-sm space-y-2">
              <li className="flex items-start gap-2">
                <span style={{color: 'var(--eventally-coral)'}}>•</span>
                <span>Explore and discover local events</span>
              </li>
              <li className="flex items-start gap-2">
                <span style={{color: 'var(--eventally-coral)'}}>•</span>
                <span>Create and manage personal events</span>
              </li>
              <li className="flex items-start gap-2">
                <span style={{color: 'var(--eventally-coral)'}}>•</span>
                <span>Chat with AI for event recommendations</span>
              </li>
              <li className="flex items-start gap-2">
                <span style={{color: 'var(--eventally-coral)'}}>•</span>
                <span>Bookmark events and sync with calendar</span>
              </li>
            </ul>
          </div>

          {/* Telegram Bot Features */}
          <div className="bg-white rounded-lg p-6 shadow-lg border">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{backgroundColor: 'var(--eventally-light-coral)'}}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'var(--eventally-coral)'}}>
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
              </div>
              <h3 className="font-semibold text-lg" style={{color: 'var(--eventally-dark)'}}>Telegram Bot</h3>
            </div>
            <ul className="text-gray-600 text-sm space-y-2 mb-4">
              <li className="flex items-start gap-2">
                <span style={{color: 'var(--eventally-coral)'}}>•</span>
                <span>Get event notifications on-the-go</span>
              </li>
              <li className="flex items-start gap-2">
                <span style={{color: 'var(--eventally-coral)'}}>•</span>
                <span>Quick event search and discovery</span>
              </li>
              <li className="flex items-start gap-2">
                <span style={{color: 'var(--eventally-coral)'}}>•</span>
                <span>AI-powered event assistance</span>
              </li>
            </ul>
            <a
              href="https://t.me/EventAllyBot"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg border hover:shadow-md transition-shadow"
              style={{color: 'var(--eventally-dark)', borderColor: 'var(--eventally-coral)'}}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
              Try Telegram Bot
            </a>
          </div>
        </div>

        {/* Screenshots Section */}
        <div className="w-full max-w-4xl">
          <div className="text-center mb-6">
            <h3 className="text-xl font-semibold mb-2" style={{color: 'var(--eventally-dark)'}}>
              See EventAlly in Action
            </h3>
            <p className="text-gray-600 text-sm">
              Discover how EventAlly makes event management effortless
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Screenshot 1 - Activity Discovery */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="aspect-[9/16] relative">
                <img
                  src="/screenshot2.jpeg"
                  alt="EventAlly Activity Discovery"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-4">
                <h4 className="font-semibold text-sm mb-2" style={{color: 'var(--eventally-dark)'}}>
                  Discover Local Events
                </h4>
                <p className="text-xs text-gray-600">
                  Browse and filter activities by category, location, and interests with our intuitive interface
                </p>
              </div>
            </div>

            {/* Screenshot 2 - Event Details */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="aspect-[9/16] relative">
                <img
                  src="/screenshot3.jpeg"
                  alt="EventAlly Event Details"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-4">
                <h4 className="font-semibold text-sm mb-2" style={{color: 'var(--eventally-dark)'}}>
                  Detailed Event Info
                </h4>
                <p className="text-xs text-gray-600">
                  Get comprehensive event details including categories, timing, location, and descriptions
                </p>
              </div>
            </div>

            {/* Screenshot 3 - Calendar Integration */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="aspect-[9/16] relative">
                <img
                  src="/screenshot1.jpeg"
                  alt="EventAlly Calendar Integration"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-4">
                <h4 className="font-semibold text-sm mb-2" style={{color: 'var(--eventally-dark)'}}>
                  Calendar Integration
                </h4>
                <p className="text-xs text-gray-600">
                  Seamlessly sync events to your personal calendar with automatic notifications and reminders
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Debug Info */}
        <div className="mt-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg w-full max-w-sm">
          <p className="text-xs text-yellow-800 text-center">
            <strong>Debug Route:</strong> /signin
          </p>
        </div>
      </div>
    </ScreenCenter>
  );
}

const CalendarDate = ({ dateString }) => {
  if (!dateString) {
    return null;
  }
  
  // Handle date format "2025-08-26 17:03:33" - convert space to T for ISO format
  const isoDateString = dateString.includes(' ') ? dateString.replace(' ', 'T') : dateString;
  const date = new Date(isoDateString);
  
  // Check if date is valid
  if (isNaN(date.getTime())) {
    console.warn('Invalid date:', dateString);
    return (
      <div className="flex flex-col items-center bg-white border rounded-lg p-2 min-w-16 shadow-sm">
        <div className="text-xs font-bold text-white px-2 py-1 rounded-t" style={{background: 'var(--eventally-coral)'}}>N/A</div>
        <div className="text-lg font-bold mt-1" style={{color: 'var(--eventally-dark)'}}>--</div>
      </div>
    );
  }
  
  const month = date.toLocaleString('default', { month: 'short' }).toUpperCase();
  const day = date.getDate();

  return (
    <div className="flex flex-col items-center bg-white border rounded-lg p-2 min-w-16 shadow-sm">
      <div className="text-xs font-bold text-white px-2 py-1 rounded-t" style={{background: 'var(--eventally-coral)'}}>{month}</div>
      <div className="text-lg font-bold mt-1" style={{color: 'var(--eventally-dark)'}}>{day}</div>
    </div>
  );
};

const BookmarkIcon = ({ isBookmarked, onClick, loading }) => {
  return (
    <button
      onClick={(e) => {
        e.stopPropagation(); // Prevent card click when clicking bookmark
        onClick();
      }}
      disabled={loading}
      className="p-1 rounded hover:bg-gray-100 transition-colors disabled:opacity-50"
      title={isBookmarked ? "Remove from bookmarks" : "Add to bookmarks"}
    >
      {isBookmarked ? (
        // Filled bookmark icon
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" className="bookmark-icon-filled" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z"/>
        </svg>
      ) : (
        // Outline bookmark icon
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" className="bookmark-icon-outline" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z"/>
        </svg>
      )}
    </button>
  );
};

const ActivityModal = ({ activity, onClose, isBookmarked, onBookmark }) => {
  if (!activity) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto relative">
        {/* Close and bookmark buttons - stacked vertically and centered */}
        <div className="absolute top-4 right-4 flex flex-col gap-2 items-center z-10">
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            title="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
          <BookmarkIcon
            isBookmarked={isBookmarked}
            onClick={() => onBookmark(activity)}
            loading={false}
          />
        </div>

        {/* Modal content */}
        <div className="p-6 pr-20">
          <div className="flex items-start gap-4 mb-4">
            <CalendarDate dateString={activity.date} />
            <div className="flex-1">
              <div className="text-xl font-semibold mb-2" style={{color: 'var(--eventally-dark)'}}>
                <div dangerouslySetInnerHTML={{ __html: activity.text_short }}></div>
              </div>
              {activity.tags && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {activity.tags.split(',').map(tag => (
                    <span 
                      key={tag.trim()} 
                      className="px-2 py-1 rounded text-sm"
                      style={{backgroundColor: 'var(--eventally-gray)', color: 'var(--eventally-dark)'}}
                    >
                      {tag.trim().replace('#', '')}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {/* Additional content area for future enhancements */}
          <div className="border-t pt-4 mt-4">
            <Link 
              to={`/${activity.id}`}
              className="eventally-button py-2 px-4 rounded-lg font-medium text-white inline-block"
            >
              Activity Page
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

function Chat({ session }) {
  const userId = session.user.id;
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState([]);
  const [activities, setActivities] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState({});
  const [showTagFilters, setShowTagFilters] = useState(false);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [bookmarkedActivities, setBookmarkedActivities] = useState(new Set());
  const [selectedActivity, setSelectedActivity] = useState(null);
  const endRef = useRef(null);

  // Load initial history
  useEffect(() => {
    if (IS_LOCAL_RUN) {
      // Skip loading messages from database in local mode
      return;
    }
    (async () => {
      const { data, error } = await supabase
        .from("messages")
        .select("id, role, content, created_at")
        .eq("user_id", userId)
        .order("created_at", { ascending: true });
      if (!error && data) setMessages(data);
    })();
  }, [userId]);

  // Subscribe to realtime for new assistant messages
  useEffect(() => {
    if (IS_LOCAL_RUN) {
      // Skip realtime subscription in local mode
      return;
    }
    const channel = supabase
      .channel("realtime:messages")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "messages",
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          setMessages((prev) => [...prev, payload.new]);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  // Load activities from localStorage on component mount
  useEffect(() => {
    const savedData = localStorage.getItem('eventally-activities-data');
    
    if (savedData) {
      try {
        const { activities, tags, timestamp } = JSON.parse(savedData);
        const now = Date.now();
        const fifteenMinutes = 15 * 60 * 1000; // 15 minutes in milliseconds
        
        // Check if data is older than 15 minutes
        if (now - timestamp > fifteenMinutes) {
          // Data expired, clear it
          localStorage.removeItem('eventally-activities-data');
        } else {
          // Data is still fresh, use it
          setActivities(activities || []);
          setTags(tags || []);
        }
      } catch (error) {
        console.error('Error loading saved activities:', error);
        localStorage.removeItem('eventally-activities-data');
      }
    }
  }, []);

  const fetchActivities = async () => {
    setLoadingActivities(true);
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
      if (!apiBaseUrl) {
        console.error('REACT_APP_API_BASE_URL not configured');
        setLoadingActivities(false);
        return;
      }
      const response = await fetch(`${apiBaseUrl}/activities`);
      if (response.ok) {
        const data = await response.json();
        const activities = data.activities || [];
        const processedTags = (data.tags || []).map(tag => tag.replace('#', ''));
        
        setActivities(activities);
        setTags(processedTags);
        setSelectedTags({});
        
        // Save to localStorage with timestamp for 15-minute expiry
        const dataToSave = {
          activities,
          tags: processedTags,
          timestamp: Date.now()
        };
        localStorage.setItem('eventally-activities-data', JSON.stringify(dataToSave));
      } else {
        console.error('Error fetching activities:', response.statusText);
      }
      setLoadingActivities(false);
    } catch (error) {
      console.error('Error:', error.message);
      setLoadingActivities(false);
    }
  };

  const handleTagChange = (event) => {
    const { name, checked } = event.target;
    trackUserAction(checked ? 'tag_filter_added' : 'tag_filter_removed', { label: name });
    setSelectedTags(prev => ({ ...prev, [name]: checked }));
  };

  const filteredActivities = activities.filter(activity => {
    const activeTags = Object.keys(selectedTags).filter(tag => selectedTags[tag]);
    if (activeTags.length === 0) {
      return true;
    }
    const activityTags = activity.tags ? activity.tags.split(',').map(t => t.trim().replace('#', '')) : [];
    return activeTags.some(tag => activityTags.includes(tag));
  });

  // Extract link from HTML content
  const extractLinkFromHTML = (htmlContent) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    const linkElement = doc.querySelector('a[href]');
    return linkElement ? linkElement.href : null;
  };

  // Toggle bookmark for an activity
  const toggleBookmark = async (activity) => {
    const activityLink = extractLinkFromHTML(activity.text_short);
    if (!activityLink) {
      console.error('No link found in activity content');
      return;
    }

    const isCurrentlyBookmarked = bookmarkedActivities.has(activity.id);
    
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
      if (!apiBaseUrl) {
        console.error('REACT_APP_API_BASE_URL not configured');
        return;
      }

      const response = await fetch(`${apiBaseUrl}/save_event`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: IS_LOCAL_RUN ? '86e4b0b2-def3-44af-949e-42db4a81b1b2' : session.user.id,
          event_type: isCurrentlyBookmarked ? "unbookmark" : "bookmark",
          event: {
            activity_link: activityLink,
            user_email: session.user.email
          }
        }),
      });

      if (response.ok) {
        trackActivityInteraction(isCurrentlyBookmarked ? 'unbookmark' : 'bookmark', activity.id);
        // Update local bookmark state
        setBookmarkedActivities(prev => {
          const newSet = new Set(prev);
          if (isCurrentlyBookmarked) {
            newSet.delete(activity.id);
          } else {
            newSet.add(activity.id);
          }
          return newSet;
        });
      } else {
        console.error('Error saving bookmark:', response.statusText);
      }
    } catch (error) {
      console.error('Error toggling bookmark:', error.message);
    }
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);

    const localMsg = {
      id: generateUUID(),
      role: "user",
      content: text,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, localMsg]);
    trackChatInteraction('message_sent', messages.length + 1);
    setInput("");

    try {
      if (IS_LOCAL_RUN) {
        // In local mode, send to dialog_webhook and use the response
        const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
        if (apiBaseUrl) {
          const response = await fetch(`${apiBaseUrl}/dialog_webhook`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              message: text,
            }),
          });
          
          if (response.ok) {
            const data = await response.json();
            // Add assistant response from API
            const assistantMsg = {
              id: generateUUID(),
              role: "assistant",
              content: data.message || `Echo: ${text}`, // fallback to echo if no message field
              created_at: new Date().toISOString(),
            };
            setMessages((prev) => [...prev, assistantMsg]);
          } else {
            // Fallback to echo on API error
            const assistantMsg = {
              id: generateUUID(),
              role: "assistant",
              content: `Echo: ${text}`,
              created_at: new Date().toISOString(),
            };
            setMessages((prev) => [...prev, assistantMsg]);
          }
        }
      } else {
        // 1) Save user message to Supabase (so history is consistent)
        await supabase.from("messages").insert({
          user_id: userId,
          role: "user",
          content: text,
        });

        // 2) Push to dialog webhook
        const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
        if (apiBaseUrl) {
          await fetch(`${apiBaseUrl}/dialog_webhook`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              message: text,
            }),
          });
        }
        // n8n should respond by posting assistant reply into Supabase (see header docs above)
      }
    } catch (e) {
      alert("Failed to send message. Check console.");
      console.error(e);
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const signOut = () => {
    trackUserAction('sign_out');
    supabase.auth.signOut();
  };

  return (
    <div className="h-screen max-h-screen flex flex-col">
      <header className="px-4 py-3 border-b flex items-center justify-between eventally-gradient-bg">
        <div className="font-semibold eventally-brand text-xl" style={{color: 'var(--eventally-dark)'}}>EventAlly Chat & Activities</div>
        <div className="flex items-center gap-3">
          {/* Mobile Profile Icon */}
          <Link 
            to="/profile" 
            className="sm:hidden p-2 rounded-lg bg-white hover:shadow-lg transition-all flex items-center justify-center"
            style={{color: 'var(--eventally-dark)'}}
            title="Profile"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
          </Link>
          
          {/* Desktop Profile Link */}
          <Link 
            to="/profile" 
            className="text-sm hidden sm:block hover:underline cursor-pointer" 
            style={{color: 'var(--eventally-dark)', opacity: '0.8'}}
          >
            {session.user.email}
          </Link>
          
          <button onClick={signOut} className="px-3 py-1.5 rounded-lg bg-white hover:shadow-lg transition-all" style={{color: 'var(--eventally-dark)'}}>
            Sign out
          </button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Activities Panel - Left Side */}
        <div className={`${SHOW_CHAT ? 'w-7/10 border-r' : 'w-full'} flex flex-col bg-gray-50`} style={{width: SHOW_CHAT ? '70%' : '100%'}}>
          <div className="p-4 border-b bg-white">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-3">
              {/* EventAlly Logo */}
              <img 
                src="/event_ally_logo.jpeg" 
                alt="EventAlly"
                className="w-12 h-12 sm:w-10 sm:h-10 object-contain rounded-lg shadow-sm"
              />
              <div className="text-center sm:text-left">
                <span style={{color: 'var(--eventally-dark)'}}>Test the bot: </span>
                <a 
                  href="https://t.me/EventAllyBot" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="eventally-brand font-semibold activity-content"
                  style={{color: 'var(--eventally-coral)', textDecoration: 'underline'}}
                >
                  EventAlly
                </a>
              </div>
            </div>
            <button 
              onClick={() => {
                fetchActivities();
                trackUserAction('explore_activities_clicked');
              }}
              disabled={loadingActivities}
              className="w-full eventally-button py-2 px-4 rounded-lg font-medium disabled:opacity-50"
            >
              {loadingActivities ? 'Loading...' : 'Explore Activities'}
            </button>
          </div>

          {tags.length > 0 && (
            <div className="border-b bg-white">
              {/* Desktop: Always show expanded, Mobile: Collapsible */}
              <div className="p-4 pb-2">
                <button
                  onClick={() => setShowTagFilters(!showTagFilters)}
                  className="w-full flex items-center justify-between text-left hover:bg-gray-50 rounded p-2 -m-2 transition-colors"
                >
                  <h5 className="text-sm font-semibold">Filter by Tags</h5>
                  <div className="flex items-center gap-2">
                    {/* Selected tags counter */}
                    {Object.values(selectedTags).filter(Boolean).length > 0 && (
                      <span 
                        className="text-xs px-2 py-1 rounded-full text-white"
                        style={{backgroundColor: 'var(--eventally-coral)'}}
                      >
                        {Object.values(selectedTags).filter(Boolean).length}
                      </span>
                    )}
                    <svg
                      className={`w-4 h-4 transition-transform duration-200 lg:hidden ${
                        showTagFilters ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>
              </div>
              
              {/* Tag filters - Always visible on desktop (lg+), collapsible on mobile */}
              <div className={`px-4 pb-4 lg:block ${showTagFilters ? 'block' : 'hidden'}`}>
                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto lg:max-h-none lg:overflow-visible">
                  {tags.map(tag => (
                    <label 
                      key={tag} 
                      className="flex items-center gap-1 text-sm whitespace-nowrap bg-gray-50 hover:bg-gray-100 px-2 py-1 rounded cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        name={tag}
                        checked={!!selectedTags[tag]}
                        onChange={handleTagChange}
                        className="rounded text-coral focus:ring-coral"
                        style={{accentColor: 'var(--eventally-coral)'}}
                      />
                      <span className="select-none">{tag}</span>
                    </label>
                  ))}
                </div>
                
                {/* Clear filters button */}
                {Object.values(selectedTags).filter(Boolean).length > 0 && (
                  <button
                    onClick={() => {
                      trackUserAction('filters_cleared');
                      setSelectedTags({});
                    }}
                    className="mt-3 text-xs text-gray-500 hover:text-gray-700 underline transition-colors"
                  >
                    Clear all filters
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="flex-1 overflow-y-auto p-4">
            {filteredActivities.length > 0 ? (
              <div className="space-y-3">
                {filteredActivities.map((activity) => (
                  <div 
                    key={activity.id} 
                    className="bg-white border rounded-lg p-3 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => {
                      setSelectedActivity(activity);
                      trackActivityInteraction('activity_viewed', activity.id);
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <CalendarDate dateString={activity.date} />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900 mb-1 activity-content">
                          <div dangerouslySetInnerHTML={{ __html: activity.text_short }}></div>
                        </div>
                        {activity.tags && (
                          <div className="text-xs text-gray-500">
                            {activity.tags.split(',').map(tag => tag.trim().replace('#', '')).join(', ')}
                          </div>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        <BookmarkIcon
                          isBookmarked={bookmarkedActivities.has(activity.id)}
                          onClick={() => toggleBookmark(activity)}
                          loading={false}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : activities.length === 0 && !loadingActivities ? (
              <div className="text-center text-gray-500 py-8">
                Click "Explore Activities" to see upcoming events
              </div>
            ) : null}
          </div>
        </div>

        {/* Chat Panel - Right Side */}
        {SHOW_CHAT && (
          <div className="w-3/10 flex flex-col" style={{width: '30%'}}>
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
              {messages.map((m) => (
                <MessageBubble key={m.id} role={m.role} content={m.content} />
              ))}
              <div ref={endRef} />
            </div>

            <footer className="p-3 border-t flex gap-2 bg-white">
              <textarea
                className="flex-1 resize-none rounded-xl border p-3 h-12 focus:outline-none focus:ring-1"
                placeholder="Type a message…"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
              />
              <button
                onClick={sendMessage}
                disabled={sending || !input.trim()}
                className="px-4 py-2 rounded-xl border hover:shadow disabled:opacity-50"
              >
                Send
              </button>
            </footer>
          </div>
        )}
      </div>
      
      {/* Activity Modal */}
      {selectedActivity && (
        <ActivityModal
          activity={selectedActivity}
          onClose={() => setSelectedActivity(null)}
          isBookmarked={bookmarkedActivities.has(selectedActivity.id)}
          onBookmark={toggleBookmark}
        />
      )}
    </div>
  );
}

function MessageBubble({ role, content }) {
  const isUser = role === "user";
  return (
    <div className={`w-full flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[80%] whitespace-pre-wrap px-3 py-2 rounded-2xl border shadow-sm ${
          isUser ? "bg-white" : "bg-gray-100"
        }`}
      >
        <div className="text-xs mb-1 opacity-70">{isUser ? "You" : "EventAlly"}</div>
        <div>{content}</div>
      </div>
    </div>
  );
}

function ScreenCenter({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-6">
      {children}
    </div>
  );
}

function LoginIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
      <polyline points="10,17 15,12 10,7"></polyline>
      <line x1="15" y1="12" x2="3" y2="12"></line>
    </svg>
  );
}

// Analytics route tracking wrapper
function AnalyticsWrapper({ children }) {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname);
  }, [location]);

  return children;
}

// Main App component with routing
export default function App() {
  useEffect(() => {
    // Initialize Google Analytics on app start
    initGA();
  }, []);

  return (
    <Router>
      <AnalyticsWrapper>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/subscription" element={<SubscriptionPage />} />
          <Route path="/signin" element={<SignInPage />} />
          <Route path="/:activityId" element={<ActivityPage />} />
        </Routes>
      </AnalyticsWrapper>
    </Router>
  );
}